import React, { useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import { Divider, Stack, Tooltip } from '@mui/material';
import CopyTypography from './CopyTypography';
import MultiStepForm from './MultiStepForm';
import styles from './styles';
import ComparePdfs from './ComparePdfs';

const colorMapping: any = {
  Deleted: 'red.main',
  Added: 'primary.main',
  ReAppeared: 'orange.main'
};
const notificationSentStatusColorMapping: any = {
  Sent: 'primary.light',
  Queued: 'orange.light',
  Rejected: 'gray.500'
};

const AccordionUsage = ({
  id,
  title,
  issueDate,
  titleLink,
  pdfUrl,
  notificationType,
  notificationSentStatus,
  editable,
  onEditSave,
  availableAtSource,
  incorrectAvailableAtSource,
  notificationCanbeSent,
  curatorNotes,
  date,
  guidancePrimaryKey,
  s3Path
}: any) => {
  const [openEditable, setOpenEditable] = useState<boolean>(false);

  const handleEditSave = (updatedData: any) => {
    onEditSave({ ...updatedData, id, guidancePrimaryKey });
    setOpenEditable(false);
  };
  const handleCurateClick = () => {
    setOpenEditable(true);
  };
  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel3-content'
        id='panel3-header'
        sx={{
          bgcolor: notificationSentStatusColorMapping[notificationSentStatus] || 'white.main'
        }}>
        <Stack direction='row' alignItems='center' width='100%'>
          <Typography flex={4} fontWeight={700}>
            {title}
          </Typography>
          <Tooltip title={`Notification type: ${notificationType}`}>
            <Typography
              component='span'
              sx={styles.statusTag}
              bgcolor={colorMapping[notificationType] || 'primary.main'}>
              {notificationType}
            </Typography>
          </Tooltip>
          {notificationSentStatus === 'Queued' && (
            <Tooltip title='Notification will be sent in the next refresh.'>
              <Typography sx={styles.statusTag} component='span' bgcolor='orange.main'>
                {notificationSentStatus}
              </Typography>
            </Tooltip>
          )}
          {notificationSentStatus === 'Rejected' && (
            <Tooltip title='This was rejected for the notification.'>
              <Typography sx={styles.statusTag} component='span' bgcolor='orange.main'>
                {notificationSentStatus}
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Divider orientation='horizontal' sx={{ borderColor: 'gray.300', my: 1, mt: 0 }} />
        <Stack
          direction='column'
          divider={<Divider orientation='horizontal' sx={{ borderColor: 'gray.300', my: 1 }} />}>
          <CopyTypography label='Title' text={title} />
          <CopyTypography label='Issue Date' text={issueDate} />
          <CopyTypography label='Title Link' text={titleLink} type='link' />
          <CopyTypography label='PDF URL' text={pdfUrl} type='link' />
          <CopyTypography
            label='Last Updated Date'
            text={new Date(date).toLocaleString('en-US')}
            type='date'
          />
          {pdfUrl.includes('http') && (
            <Stack direction='row' spacing={2} alignItems='center'>
              <ComparePdfs fdaURL={pdfUrl} riaPath={s3Path} />
              <Typography component='span'>
                <Typography component='span' fontWeight={600}>
                  NOTE:
                </Typography>
                If you find the changes between the documents. Please escalate it to developers.
              </Typography>
            </Stack>
          )}
        </Stack>
        <Divider orientation='horizontal' sx={{ borderColor: 'gray.300', my: 1 }} />
      </AccordionDetails>
      {editable && (
        <AccordionActions sx={{ justifyContent: openEditable ? 'center' : 'end' }}>
          {!openEditable ? (
            <Button onClick={handleCurateClick}>Curate</Button>
          ) : (
            <MultiStepForm
              notificationType={notificationType}
              onSubmit={handleEditSave}
              availableAtSource={availableAtSource}
              incorrectAvailableAtSourceFlag={incorrectAvailableAtSource}
              notificationCanbeSentFlag={notificationCanbeSent}
              curatorNotes={curatorNotes}
            />
          )}
        </AccordionActions>
      )}
    </Accordion>
  );
};

export default React.memo(AccordionUsage);
